// Decode a file to per-channel 16 kHz mono PCM, for callers that want to keep
// channels separate rather than downmixing (decodeAudio.js's job) — namely,
// transcribing each channel of a 2-channel call recording independently, so
// "left channel" / "right channel" becomes a free, deterministic speaker
// split instead of losing that information before Whisper ever sees it.
//
// Deliberately composed from two already-proven modules rather than adding a
// third copy of the tier-selection logic (native WAV, G.729, browser
// decodeAudioData, ffmpeg.wasm fallback):
//   - decodeToOriginal (decodeOriginalAudio.js) already returns undownmixed,
//     unresampled per-channel audio across every decode tier.
//   - resampleChannels (resampleChannels.js) already resamples an arbitrary
//     number of channels together via one OfflineAudioContext render.
// Both are already used together by the Audio Format Converter tool.

import { decodeToOriginal } from './decodeOriginalAudio';
import { resampleChannels } from './resampleChannels';
import { SAMPLE_RATE } from './whisperModels';

/**
 * @param {File} file
 * @param {(msg: object) => void} [onEvent] progress sink for the ffmpeg
 *   fallback only, forwarded from decodeToOriginal — see decodeAudio.js.
 * @returns {Promise<{ channels: Float32Array[], duration: number, sourceRate: number, sourceFormat: string|null }>}
 *   Each entry of `channels` is independently resampled to 16 kHz mono — no
 *   downmix. `sourceRate` is the original pre-resample rate, matching
 *   decodeToMono16k's convention (so a caller keying UI off it, like the
 *   narrowband caveat, needs no changes).
 */
export async function decodeToChannels16k(file, onEvent = () => {}) {
  const { channels, sampleRate, sourceFormat } = await decodeToOriginal(file, onEvent);
  const resampled = await resampleChannels(channels, sampleRate, SAMPLE_RATE);
  return {
    channels: resampled,
    duration: resampled[0].length / SAMPLE_RATE,
    sourceRate: sampleRate,
    sourceFormat,
  };
}

/** Average N channels down to one — the non-2-channel path (mono passthrough,
 *  or 3+ channels where a per-speaker split isn't attempted; see Transcribe.js). */
export function mixDownChannels(channels) {
  if (channels.length === 1) return channels[0];

  const len = channels[0].length;
  const out = new Float32Array(len);
  for (const data of channels) {
    for (let i = 0; i < len; i++) out[i] += data[i];
  }
  for (let i = 0; i < len; i++) out[i] /= channels.length;
  return out;
}
